import { Capacitor } from "@capacitor/core";
import { NFC } from "@ionic-native/nfc";

/**
 * NFC NDEF record.
 */
export interface NdefRecord {
  id: number[];
  payload: number[];
  tnf: number;
  type: number[];
}

/**
 * NFC tag data.
 */
export interface NfcTag {
  id?: number[];
  ndefMessage?: NdefRecord[];
  techTypes?: string[];
  type?: string;
  maxSize?: number;
  isWritable?: boolean;
  canMakeReadOnly?: boolean;
}

/**
 * NFC reader mode flags.
 */
export const NFC_FLAG_READER_NFC_A = 0x1;
export const NFC_FLAG_READER_NFC_V = 0x20;

/**
 * Converts a byte array to a string.
 *
 * @param bytes - The byte array to convert
 * @returns The string representation
 */
export function nfcBytesToString(bytes: number[]): string {
  return NFC.bytesToString(bytes);
}

/**
 * Checks if NFC is enabled on the device.
 * On native platforms, checks NFC hardware status.
 * On web, throws "NO_NFC".
 *
 * @throws "NO_NFC" if device has no NFC hardware
 * @throws "NFC_DISABLED" if NFC is disabled
 */
export async function checkNfcEnabled(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    // oxlint-disable-next-line no-throw-literal -- Match the plugin's error format
    throw "NO_NFC";
  }

  await NFC.enabled();
}

/**
 * Scans for an NDEF tag (iOS).
 * On native platforms (iOS), opens the NFC scanner.
 * On web, throws an error.
 *
 * @returns The scanned NFC tag
 */
export async function scanNdefTag(): Promise<NfcTag> {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("NFC scanning is only available on native platforms");
  }

  return await NFC.scanNdef();
}

/**
 * Starts NFC reader mode (Android).
 * On native platforms (Android), starts listening for NFC tags.
 * On web, throws an error.
 *
 * @param flags - Reader mode flags (use NFC_FLAG_READER_NFC_A | NFC_FLAG_READER_NFC_V)
 * @returns Observable that emits when a tag is read
 */
export async function startNfcReaderMode(flags: number): Promise<{
  subscribe: (onData: (tag: NfcTag) => void, onError: (error: unknown) => void) => void;
}> {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("NFC reader mode is only available on native platforms");
  }

  return NFC.readerMode(flags);
}

/**
 * Cancels the current NFC scan.
 * On native platforms, stops the NFC scanner.
 * On web, this is a no-op.
 */
export async function cancelNfcScan(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await NFC.cancelScan();
}
